import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useAuth } from "../contexts/AuthContext";
import "./Auth.css";

export default function SignupPage() {
  const navigate = useNavigate();
  const { signup } = useAuth();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const adminEmails = [
    "admin@dshb.org",
    "ikavyach@gmail.com",
  ];

  // ── OTP verification is disabled — email sending is currently blocking
  // on the server, so signup completes immediately after the email-exists
  // check, with no OTP step in between. ───────────────────────────────
  const handleSignup = async (e) => {
    e.preventDefault();
    if (!firstName || !lastName || !email || !password) {
      return toast.error("All fields are required");
    }

    setLoading(true);

    try {
      console.log("🔍 Checking if email exists...");
      const checkRes = await fetch("https://dshb-deposits.its.uiowa.edu/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password: "dummy" }),
      });

      const checkData = await checkRes.json();

      if (checkData.error !== "Email not registered") {
        console.log("❌ Email already exists");
        toast.error("Email already exists. Please login.");
        navigate("/");
        setLoading(false);
        return;
      }

      console.log("✅ Email is available");

      /* ── OTP step disabled ────────────────────────────────────────
      console.log("📧 Sending OTP...");
      const otpRes = await fetch("https://dshb-deposits.its.uiowa.edu/api/request-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const otpData = await otpRes.json();
      if (!otpRes.ok) {
        console.error("❌ OTP send failed:", otpData.error);
        toast.error(otpData.error || "Failed to send OTP");
        setLoading(false);
        return;
      }

      console.log("✅ OTP sent successfully");
      toast.success("OTP sent to your email");

      const tempUser = { firstName, lastName, email, password };
      localStorage.setItem("tempUser", JSON.stringify(tempUser));
      console.log("💾 Temp user saved to localStorage");

      setStep(2);
      ──────────────────────────────────────────────────────────────── */

      console.log("📝 Creating account directly (OTP skipped)...");
      const isAdmin = adminEmails.includes(email);
      const userRole = isAdmin ? "admin" : "depositor";

      const signupResult = await signup({
        firstName,
        lastName,
        email,
        password,
        role: userRole,
      });

      console.log("Signup result:", signupResult);

      if (!signupResult.success) {
        console.error("❌ Signup failed:", signupResult.message);
        toast.error(signupResult.message || "Signup failed");
        setLoading(false);
        return;
      }

      toast.success("Account created successfully! Redirecting...");
      console.log("✅ SIGNUP COMPLETE");

      setTimeout(() => {
        navigate(userRole === "admin" ? "/admin" : "/dashboard");
      }, 1000);

    } catch (err) {
      console.error("❌ Error:", err.message);
      toast.error("Error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  /* ── OTP verify handler disabled along with the OTP step ──────────
  const verifyOtp = async (e) => {
    e.preventDefault();
    if (!otp) return toast.error("Please enter the OTP");

    setLoading(true);

    try {
      console.log("🔍 Verifying OTP...");
      const verifyRes = await fetch("https://dshb-deposits.its.uiowa.edu/api/verify-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code: otp }),
      });

      const verifyData = await verifyRes.json();
      if (!verifyRes.ok) {
        console.error("❌ OTP verification failed:", verifyData.error);
        toast.error(verifyData.error || "OTP verification failed");
        setLoading(false);
        return;
      }

      console.log("✅ OTP verified");
      toast.success("OTP verified. Creating your account...");

      const tempUser = JSON.parse(localStorage.getItem("tempUser"));
      const isAdmin = adminEmails.includes(tempUser.email);
      const userRole = isAdmin ? "admin" : "depositor";

      console.log("👤 User role determined:", userRole);

      console.log("📝 Calling signup function...");
      const signupResult = await signup({
        firstName: tempUser.firstName,
        lastName: tempUser.lastName,
        email: tempUser.email,
        password: tempUser.password,
        role: userRole,
      });

      console.log("Signup result:", signupResult);

      if (!signupResult.success) {
        console.error("❌ Signup failed:", signupResult.message);
        toast.error(signupResult.message || "Signup failed");
        setLoading(false);
        return;
      }

      localStorage.removeItem("tempUser");
      console.log("🧹 Temp user cleared");

      toast.success("Account created successfully! Redirecting...");
      console.log("✅ SIGNUP COMPLETE");

      setTimeout(() => {
        navigate(userRole === "admin" ? "/admin" : "/dashboard");
      }, 1000);

    } catch (err) {
      console.error("❌ Error:", err.message);
      toast.error("Error: " + err.message);
    } finally {
      setLoading(false);
    }
  };
  ──────────────────────────────────────────────────────────────────── */

  return (
    <div className="auth-page-wrapper">

      <div className="auth-image-section">
        <img
          src={`${process.env.PUBLIC_URL}/mf20.png`}
          alt="MF20 Antibody - Myosin Heavy Chain"
          className="antibody-image"
        />
        <div className="image-overlay"></div>
        <div className="image-label">

            <a href="https://sb3-dshb.biology.uiowa.edu/MF-20"
            target="_blank"
            rel="noopener noreferrer"
            className="label-antibody-link"
          >
            <p className="label-title">MF 20↗</p>
          </a>
          <p className="label-subtitle">Sarcomeric myosin heavy chain (MYH1E)</p>
          <div className="label-meta">
            <p className="label-depositor">Depositor: D.A Fischman (1986)</p>
          </div>
        </div>

      </div>
      <div className="auth-form-section">
      <div className="auth-container">
        <div className="back-icon" onClick={() => navigate("/")}>←</div>

        <div className="auth-header">
          <div className="header-with-logo">
              <img src={`${process.env.PUBLIC_URL}/dshb-logo.png`} alt="DSHB Logo" className="header-logo" />
              <div className="header-text">
                <h1 className="portal-title">DSHB Depositor Portal</h1>
              </div>
            </div>
            <p>Sign Up</p>
        </div>

        <form onSubmit={handleSignup}>
          <div className="form-group">
            <label>First Name <span className="required">*</span></label>
            <input
              type="text"
              placeholder="Enter your first name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label>Last Name <span className="required">*</span></label>
            <input
              type="text"
              placeholder="Enter your last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label>Email Address <span className="required">*</span></label>
            <input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
            />
          </div>

          <div className="form-group password-field">
            <label>Password <span className="required">*</span></label>
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Create a password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
            />
            <span
              className="eye-icon"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </span>
          </div>

          <button type="submit" disabled={loading}>
            {loading ? "Creating Account..." : "Create Account"}
          </button>
        </form>

        <div className="signup-link">
          Already have an account? <Link to="/">Sign in here</Link>
        </div>

      </div>
      </div>
    </div>
  );
}